import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

import {
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ExperimentalInputs,
  ExperimentalInputsContextProperties,
} from 'procedures/planning/ProcedurePlanTypes';
import {TimingEstimateResult} from 'studies/progress/useTimingEstimate';
import {Condition} from 'studies/typed/process-development/useProcessDevelopmentStudyState';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import {GRAY_LIGHT, NEAR_BLACK} from 'ui-library/Colors';

import MultiBatchButton from '../process-development/multi-batch-selector/MultiBatchButton';
import MultiBatchSelector, {
  MULTI_BATCH_BUTTON_CONFIG_BY_DATA_ENTRY_MODE,
} from '../process-development/multi-batch-selector/MultiBatchSelector';
import {getTypedStudyAdapter} from '../TypedStudyAdapters';
import {EditNumConditionsButtons} from './EditNumConditionsButtons';
import {ExperimentFlowchart, ExperimentMethod} from './ExperimentalInputs';
import MethodExecutionLimitError from './MethodExecutionLimitError';
import ProcedureRunPlan, {ExperimentalInputsContext} from './ProcedureRunPlan';
import {ReplicateTable} from './ReplicateTable';
import {MethodExecutionLimitState} from './useMethodExecutionLimitState';

/** Tab name constant for the conditions/parameter values tab */
export const PARAMETER_VALUES_TAB_NAME = 'Conditions';

/** Tab name constant for the settings tab */
export const SETTINGS_TAB_NAME = 'Settings';

export const MULTI_BATCH_BUTTON_RUN_MESSAGE =
  'Based on your earlier selection, all worksheets will be created with the following data entry mode.';
export const MULTI_BATCH_BUTTON_PLAN_MESSAGE =
  'Based on your selection, all worksheets will be created with the following data entry mode. Click the edit button to change the data entry mode.';

/**
 * Presentational component for the parameter values (conditions) tab.
 * Used by both MultiProcedureRunStudyDesignTabContents and MultiProcedureRunPlanStudyDesignTabContents.
 */
export function ParameterValuesTabContents(props: {
  methods: Array<ExperimentMethod>;
  procedureFlowchart: ExperimentFlowchart;
  selectedMethodId: string;
  onChangeSelectedMethod: (newMethodId: string) => void;
  procedureType: ProcedureType;
  context: ExperimentalInputsContextProperties;
}): JSX.Element {
  return (
    <div className={css(studyDesignTabStyles.experimentPlan)}>
      <ExperimentalInputsContext.Provider value={props.context}>
        <ProcedureRunPlan
          methods={props.methods}
          procedureFlowchart={props.procedureFlowchart}
          selectedMethodId={props.selectedMethodId}
          onChangeSelectedMethod={props.onChangeSelectedMethod}
          procedureType={props.procedureType}
        />
      </ExperimentalInputsContext.Provider>
    </div>
  );
}

interface GetMultiBatchButtonOrSelectorPlanProps {
  dataEntryMode: ProcedureRunDataEntryMode;
  onSelectDataEntryMode: (mode: ProcedureRunDataEntryMode) => void;
  isDisabled: boolean;
  isPlan: true;
}

interface GetMultiBatchButtonOrSelectorRunProps {
  dataEntryMode: ProcedureRunDataEntryMode;
  isPlan: false;
}

function MultiBatchButtonComponent(props: {dataEntryMode: ProcedureRunDataEntryMode}): JSX.Element {
  const multiBatchButtonConfig = MULTI_BATCH_BUTTON_CONFIG_BY_DATA_ENTRY_MODE[props.dataEntryMode];

  return (
    <div className={css(studyDesignTabStyles.multiBatchButtonContainer)}>
      <div className={css(studyDesignTabStyles.dataEntryModeLabel)}>Data entry mode</div>
      <div className={css(studyDesignTabStyles.dataEntryModeDescription)}>
        {MULTI_BATCH_BUTTON_RUN_MESSAGE}
      </div>
      <div className={css(studyDesignTabStyles.multiBatchButton)}>
        <MultiBatchButton
          graphic={multiBatchButtonConfig.graphic}
          headerText={multiBatchButtonConfig.headerText}
          description={multiBatchButtonConfig.description}
          dataTestAttrs={{component: 'MultiBatchButton'}}
          onSelect={noop}
          isDisabled={true}
          active={false}
          includeRadioButton={false}
        />
      </div>
    </div>
  );
}

function getMultiBatchButtonOrSelector(
  props: GetMultiBatchButtonOrSelectorPlanProps | GetMultiBatchButtonOrSelectorRunProps
): JSX.Element {
  return props.isPlan ? (
    <MultiBatchSelector
      dataEntryMode={props.dataEntryMode}
      onSelectDataEntryMode={props.onSelectDataEntryMode}
      isDisabled={props.isDisabled}
      text={MULTI_BATCH_BUTTON_PLAN_MESSAGE}
    />
  ) : (
    <MultiBatchButtonComponent dataEntryMode={props.dataEntryMode} />
  );
}

interface SettingsTabContentsCommonProps {
  conditions: Array<Condition>;
  setConditions: (conditions: Array<Condition>, prevIndexes: Array<number | null>) => void;
  selectedMethodId: string;
  experimentalInputs: ExperimentalInputs | null;
  setExperimentalInputs: (experimentalInputs: ExperimentalInputs) => void;
  locked: boolean;
  procedureType: ProcedureType;
  methodExecutionLimitState: MethodExecutionLimitState;
  dataEntryMode: ProcedureRunDataEntryMode;
  studyType: StudyType;
  estimateResult: TimingEstimateResult;
}

interface SettingsTabContentsPlanProps extends SettingsTabContentsCommonProps {
  isPlan: true;
  setDataEntryMode: (mode: ProcedureRunDataEntryMode) => void;
}

interface SettingsTabContentsRunProps extends SettingsTabContentsCommonProps {
  isPlan: false;
}

/**
 * Presentational component for the settings tab.
 * Used by both MultiProcedureRunStudyDesignTabContents and MultiProcedureRunPlanStudyDesignTabContents.
 */
export function SettingsTabContents(
  props: SettingsTabContentsPlanProps | SettingsTabContentsRunProps
): JSX.Element {
  const editNumColsButtons = (
    <EditNumConditionsButtons
      conditions={props.conditions}
      setConditions={props.setConditions}
      methodExecutionLimitState={props.methodExecutionLimitState}
      procedureType={props.procedureType}
    />
  );

  return (
    <div className={css(studyDesignTabStyles.settingsTabContainer)}>
      <ReplicateTable
        conditions={props.conditions}
        setConditions={props.setConditions}
        selectedMethodId={props.selectedMethodId}
        experimentalInputs={props.experimentalInputs}
        setExperimentalInputs={props.setExperimentalInputs}
        editNumColsButtons={editNumColsButtons}
        locked={props.locked}
        estimate={props.estimateResult}
        procedureType={props.procedureType}
        isPlan={props.isPlan}
        dataTestAttrs={{component: 'ReplicateTable', element: 'replicateTable'}}
      />
      {props.methodExecutionLimitState.isOverLimit && (
        <div className={css(studyDesignTabStyles.methodExecutionLimitError)}>
          <MethodExecutionLimitError
            replicateLimit={props.methodExecutionLimitState.replicateLimit}
            procedureType={props.procedureType}
          />
        </div>
      )}
      {getTypedStudyAdapter(props.studyType).isMultiBatchEnabled &&
        (props.isPlan
          ? getMultiBatchButtonOrSelector({
              dataEntryMode: props.dataEntryMode,
              onSelectDataEntryMode: props.setDataEntryMode,
              isDisabled: props.locked,
              isPlan: true,
            })
          : getMultiBatchButtonOrSelector({
              dataEntryMode: props.dataEntryMode,
              isPlan: false,
            }))}
    </div>
  );
}

export interface ContextPanelProps {
  sidepanelName: string;
  sidepanelHeaderContent?: JSX.Element;
  /** Content to render inside the sidepanel */
  children: ReactNode;
}

/**
 * Presentational component for the context panel sidebar.
 * Used by both MultiProcedureRunStudyDesignTabContents and MultiProcedureRunPlanStudyDesignTabContents.
 */
export function ContextPanel({
  children,
  sidepanelName,
  sidepanelHeaderContent,
}: ContextPanelProps): JSX.Element | null {
  return (
    <div className={css(studyDesignTabStyles.sidepanel)}>
      <div className={css(studyDesignTabStyles.sidepanelHeader)}>
        <h2 className={css(studyDesignTabStyles.sidepanelName)}>{sidepanelName}</h2>
        {sidepanelHeaderContent}
      </div>
      <div className={css(studyDesignTabStyles.sidepanelContent)}>{children}</div>
    </div>
  );
}

/**
 * Shared styles for study design tab contents.
 * Used by both MultiProcedureRunStudyDesignTabContents and MultiProcedureRunPlanStudyDesignTabContents.
 */
export const studyDesignTabStyles = StyleSheet.create({
  bannerAndButtons: {display: 'flex', alignItems: 'center'},
  bufferTabWrapper: {paddingTop: 10, paddingBottom: 10},
  dataEntryModeLabel: {...FONT_PROXIMA_TITLE, color: NEAR_BLACK, marginBottom: 8},
  dataEntryModeDescription: {color: NEAR_BLACK, marginBottom: 12},
  designBackground: {background: Color.WHITE},
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 33px 8px 12px',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  settingsTabContainer: {display: 'flex', flexDirection: 'column', flex: 1},
  experimentPlan: {minHeight: 0, flex: 1},
  leftSidepanelContainer: {display: 'flex', position: 'absolute', height: '100%', width: '100%'},
  methodExecutionLimitError: {display: 'flex', marginLeft: '10px'},
  multiBatchButton: {backgroundColor: Color.GRAY_50},
  multiBatchButtonContainer: {marginTop: '20px', marginLeft: '10px', width: '555px'},
  // sidepanel styling is aligned with FlowchartContextPanel
  sidepanel: {backgroundColor: Color.WHITE, flex: 1, display: 'flex', flexDirection: 'column'},
  sidepanelContent: {flex: 1},
  sidepanelHeader: {
    padding: '12px 12px',
    borderBottom: `1px solid ${Color.GRAY_200}`,
    display: 'flex',
    alignItems: 'center',
  },
  sidepanelName: {margin: 0, fontSize: '13px', fontWeight: 600},
  tabTitle: {
    color: NEAR_BLACK,
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '16px',
    display: 'flex',
    alignItems: 'center',
  },
});
